import { InjectableClass } from '@fino/lib-injection';
import _ from 'lodash';
import { ProductType } from './ticket.constants';

const ChangeTypes = {
  Ticket: 'ticket',
  ViewStatus: 'viewStatus',
};

// TODO MAKE TICKETSERVICE STATELESS!
// Idea: Make a ticketChangedById function which just calls the callback
// when the ticket with given id changes
export class TicketService extends InjectableClass {
  constructor(
    User,
    userService,
    envService,
    appCache,
    appConfig,
    ticketFlowService,
    ticketsService,
    ticketReaderFactory,
    progressService,
    ExternalLinking,
    TicketType,
    TicketRequestType,
    TicketRequestTypeMap,
    $q,
    $stateParams,
  ) {
    'ngInject';
    super({
      User,
      userService,
      envService,
      appCache,
      appConfig,
      ticketFlowService,
      ticketsService,
      ticketReaderFactory,
      progressService,
      ExternalLinking,
      TicketType,
      TicketRequestType,
      TicketRequestTypeMap,
      $q,
      $stateParams,
    });

    this.clear();
  }

  get ViewStatus() {
    return this.progressService.ViewStatus;
  }

  get ChangeTypes() {
    return ChangeTypes;
  }

  get TicketStates() {
    return this.ticketFlowService.TicketStates;
  }

  get ticketDataService() {
    return this.ticketsService.ticketDataService;
  }

  getViewStatus(routes, ticket) {
    // Ignore the view status for revision user
    if (this.userService.isRevision()) {
      return null;
    }

    // Return the actual ticket status when the ticket is locked
    // NOTE: For the current ticket owner the ticket is NOT locked.
    if (this.isTicketLocked(ticket)) {
      return ticket.status;
    }

    return this.progressService.getStatusForCurrentState(routes, ticket);
  }

  getStateData(ticket) {
    return {
      ticketId: ticket.id,
    };
  }

  aidRequestUpdatedResponse(ticketId) {
    return (aidRequest) => {
      if (aidRequest) {
        this.ticketChanged(ticketId, aidRequest);
      }
      return aidRequest;
    };
  }

  get refreshResponse() {
    return (data) => {
      return this.refresh(data);
    };
  }

  getRefreshResponse(
    ticketId,
    ignoreRefresh = false,
    product = ProductType.KWH,
  ) {
    if (ignoreRefresh) {
      return (data) => this.$q.when(data);
    }
    return (data) => {
      return this.load(ticketId, product).then(() => {
        return data;
      });
    };
  }

  static aidRequest(options = {}) {
    return (
      $state,
      $stateParams,
      $q,
      ticketService,
      ticketsDataService,
      ticketReaderFactory,
      TicketRequestTypeMap,
      userService,
      errorTrackingService,
      appCache,
    ) => {
      'ngInject';
      const { ticketId, product } = $stateParams;

      // If no ticketId is given and the ticket is optional
      // resolve null to handle the optional resolver.
      if (!ticketId && options.optional) {
        return $q.resolve(null);
      }

      // Associate error tracking with given ticketId
      errorTrackingService.setUserContext(
        _.merge(
          {
            ticketId,
          },
          userService.get(),
        ),
      );
      const result = ticketsDataService
        .one(ticketId, product)
        .catch((error) => {
          const { code, message, prefix } = error.data;
          const ticketError = { code, message, prefix };

          // Handle error for failed ticket request gracefully
          $state.go('app.ticketError', { ticketId, ticketError });
        });

      // Handle static request of ticket without lock handling
      if (options.static) {
        return result.then((aidRequest) =>
          ticketReaderFactory.createForDetail(aidRequest),
        );
      }

      return result.then((aidRequest) => {
        // In case there is no aidRequest given, resolve an empty result.
        if (!aidRequest) {
          return $q.resolve(aidRequest);
        }

        // Check if the ticket is currently locked
        const lock = ticketService.getTicketLock(aidRequest);
        if (lock) {
          // NOTE: At this point we know that the requested ticket
          // is actually locked by another user.
          // Instead of rejecting the request, we enrich the resolved
          // aidRequest with the lock information and resolve it.
          // Therefore the requesting controller can handle the
          // locked ticket and the user will not remain on a blank page.
          return $q.resolve(
            ticketReaderFactory.createForLocked(aidRequest, lock),
          );
        }

        // TODO in case we want to implicetly lock tickets on their request,
        // we can do this here.
        // const { requestType } = aidRequest;
        // if (ticketService.hasPermissionToLock(requestType)) {
        //   // TODO Lock ticket
        //   // ticketService.lockTicket(requestType, aidRequest.id);
        // }

        // Return the aidRequest as requested
        return aidRequest;
      });
    };
  }

  static ticketId(options = {}) {
    return (
      $state,
      $stateParams,
      $q,
      ticketsDataService,
      ticketReaderFactory,
    ) => {
      'ngInject';
      return $stateParams.ticketId;
    };
  }

  static ticket(options = {}) {
    options.static = true;
    return TicketService.aidRequest(options);
  }

  clear() {
    this.listeners = {};
  }

  fetchTicket(ticketId, product = ProductType.KWH) {
    return this.ticketDataService.one(ticketId, product);
  }

  load(ticketId, product = ProductType.KWH) {
    return this.fetchTicket(ticketId, product).then((aidRequest) => {
      this.ticketChanged(ticketId, aidRequest);
      return aidRequest;
    });
  }

  updateTicket(aidRequest, newAidRequestData) {
    // TODO Call specific completed callback when completed.
    // Also do this for closed state

    // Update the ticket data
    _.assign(aidRequest, newAidRequestData);
    const ticket = this.ticketReaderFactory.createForDetail(aidRequest);

    // Update (ticket-)routes which depends on the ticket status
    let routes = this.ticketFlowService.getRoutesForTicket(
      ticket,
      this.$stateParams.product,
    );

    // Filter routes by removing the route(s) that depends on specific route conditions (e.g. closeAccount)
    const routeConditions = this.ticketFlowService.getRouteConditions(ticket);
    if (routeConditions) {
      routes = _.reject(routes, (route) => {
        if (route.reject) {
          // Find any match between the routeConditions and the given route rejects
          return _.some(route.reject, (val, key) => {
            return _.some(routeConditions, (val2, key2) => {
              return key === key2 && val === val2;
            });
          });
        }
      });
    }

    const ticketRoutes = _.filter(routes, (route) =>
      _.includes(route.states, ticket.status),
    );

    return {
      aidRequest,
      ticket,
      routes,
      ticketRoutes,
      routeConditions,
    };
  }

  lockTicket(ticketType, ticketId) {
    // Check permission for (un)locking a ticket
    if (!this.hasPermissionToLock(ticketType)) {
      return this.$q.resolve(false);
    }
    return this.ticketDataService.lock(ticketId);
  }

  releaseTicket(ticketType, ticketId, releaseByOwner = false) {
    // Check permission for (un)locking a ticket
    if (!this.hasPermissionToLock(ticketType)) {
      return this.$q.resolve(false);
    }
    return this.ticketDataService.release(ticketId);
  }

  completeDWS(ticketId) {
    return this.ticketDataService
      .completeDWS(ticketId)
      .then(this.getRefreshResponse(ticketId, false, ProductType.DWS));
  }

  cancelDWS(ticketId) {
    return this.ticketDataService
      .cancelDWS(ticketId)
      .then(this.getRefreshResponse(ticketId, false, ProductType.DWS));
  }

  registerForChange(callback, id) {
    if (angular.isFunction(callback)) {
      if (!this.listeners[id]) {
        this.listeners[id] = [];
      }
      this.listeners[id].push(callback);

      return () => {
        // _.remove fails here...
        const index = this.listeners[id].indexOf(callback);
        if (index > -1) {
          this.listeners[id].splice(index, 1);
        }
      };
    }
    return angular.noop;
  }

  ticketChanged(id, newAidRequestData) {
    // Inform all listeners about the changed ticket
    _.forEach(this.listeners[id], (listener) =>
      listener(this.ChangeTypes.Ticket, newAidRequestData),
    );
  }

  viewStatusChanged(id, options) {
    options = _.merge(
      {},
      { reason: 'noReason', status: 'custom' },
      options || {},
    );
    // Inform all listeners about the changed view status
    _.forEach(this.listeners[id], (listener) =>
      listener(this.ChangeTypes.ViewStatus, options),
    );
  }

  isTicketClosed(ticket) {
    // The ticket is closed either when it was closed by customer
    // or when the request has been completed.
    return this.hasStatus(ticket, [
      this.TicketStates.REJECTED_REQUEST,
      this.TicketStates.CLOSED_BY_CUSTOMER,
      this.TicketStates.REQUEST_COMPLETED,
      this.TicketStates.RECEIVED_REJECTION,
      this.TicketStates.FINANCES_VERIFIED,
      this.TicketStates.TRANSACTION_DATA_SENT,
      this.TicketStates.TRANSACTION_DATA_SENT_VIA_FAX,
    ]);
  }

  /**
   * Returns true when the given ticket has on of the given status.
   * As status you can pass one (as string) or multiple (as array) status.
   */
  hasStatus(ticket, status) {
    const ticketState = ticket.status || ticket.currentStatus;
    if (angular.isArray(status)) {
      return status.indexOf(ticketState) > -1;
    }
    return ticketState === status;
  }

  /**
   * Returns true if ticket status history contains given status.
   * @param ticket
   * @param status - TicketStatesReceiving
   */
  statusHistoryContains(ticket, status) {
    return angular.isDefined(
      _.find(ticket.history, (h) => {
        return h.eventName === status;
      }),
    );
  }

  hasPermissionToLock(requestType) {
    const { userService, TicketRequestTypeMap, TicketRequestType } = this;
    const type = TicketRequestType[requestType] || requestType;
    const permission = userService.Permissions[TicketRequestTypeMap[type]];
    if (permission) {
      return userService.hasPermission([
        permission.Handle,
        permission.CaptureTransactions,
        permission.Complete,
      ]);
    }
    return false;
  }

  canUploadTransferringData() {
    return (
      this.$stateParams.upload ||
      (this.userService.isBackOffice() &&
        this.envService.isTenant(this.envService.custom.commerzbank)) ||
      ((this.userService.isBackOffice() ||
        this.userService.isBackOfficeDirect()) &&
        this.envService.isTenant(this.envService.custom.apobank))
    );
  }

  canHandleTicket() {
    // TODO (AV) Improve this permission check, currently it
    // only ensures that the revision user role cannot handle
    // a ticket.
    return !this.userService.isRevision();
  }

  isTicketLocked(ticket) {
    return this.getTicketLock(ticket) !== null;
  }

  getTicketLock(ticket) {
    const { lockedAt, lockedBy, lockExpiresAt, disableLocks } = ticket;

    //locking not needed
    if (disableLocks) {
      return null;
    }
    // Check if a maybe given ticket lock has not been expired
    // and if the ticket lock creator himself does not request
    // the ticket.
    const expired = new Date(lockExpiresAt) < new Date();
    const owner = lockedBy === this.userService.getSub();
    if (!expired && !owner) {
      return { lockedAt, lockedBy, lockExpiresAt };
    }
    return null;
  }

  getWarrantUrl(ticketId) {
    return this.ticketDataService.getWarrantUrl(ticketId);
  }

  getSignedWarrantUrl(ticketId) {
    return this.ticketDataService.getSignedWarrantUrl(ticketId);
  }

  uploadSignedWarrant(file, ticketId, force = false) {
    return this.ticketDataService
      .uploadSignedWarrant(file, ticketId, force)
      .then(this.getRefreshResponse(ticketId));
  }

  uploadSterbeurkunde(file, ticketId) {
    return this.ticketDataService
      .uploadSterbeurkunde(file, ticketId)
      .then(this.getRefreshResponse(ticketId));
  }

  uploadSignedWarrantOutgoing(file) {
    return this.ticketDataService.uploadSignedWarrantOutgoing(file);
  }

  createOutgoing(data) {
    return this.ticketDataService.createOutgoing(data);
  }

  createOutgoingWithSignedWarrant(file, entryDate) {
    return this.ticketDataService.createOutgoingWithSignedWarrant(
      file,
      entryDate,
    );
  }

  confirmOutgoing(ticketId, data) {
    return this.ticketDataService.confirmOutgoing(ticketId, data);
  }

  acceptOutgoing(ticketId, data) {
    return this.ticketDataService
      .acceptOutgoing(ticketId, data)
      .then(this.getRefreshResponse(ticketId));
  }

  reject(ticketId, data) {
    return this.ticketDataService
      .reject(ticketId, data)
      .then(this.getRefreshResponse(ticketId));
  }

  rejectSignedWarrant(ticketId, reasons) {
    return this.ticketDataService
      .rejectSignedWarrant(ticketId, reasons)
      .then(this.getRefreshResponse(ticketId));
  }

  uploadReceivedPayments(ticketId, file) {
    return this.ticketDataService
      .uploadReceivedPayments(ticketId, file)
      .then(this.getRefreshResponse(ticketId));
  }

  uploadSepaDirectDebits(ticketId, file) {
    return this.ticketDataService
      .uploadSepaDirectDebits(ticketId, file)
      .then(this.getRefreshResponse(ticketId));
  }

  uploadAllTransactionsParallelZiraat(
    ticketId,
    partners,
    standingOrders,
    transactions,
  ) {
    return this.ticketDataService
      .uploadAllTransactionsParallelZiraat(
        ticketId,
        partners,
        standingOrders,
        transactions,
      )
      .then(this.getRefreshResponse(ticketId));
  }

  uploadStandingOrders(ticketId, file) {
    return this.ticketDataService
      .uploadStandingOrders(ticketId, file)
      .then(this.getRefreshResponse(ticketId));
  }

  uploadScheduledTransaction(ticketId, file) {
    return this.ticketDataService
      .uploadScheduledTransaction(ticketId, file)
      .then(this.getRefreshResponse(ticketId));
  }

  uploadBalanceList(ticketId, file, additionalFile) {
    return this.ticketDataService
      .uploadBalanceList(ticketId, file, additionalFile)
      .then(this.getRefreshResponse(ticketId));
  }

  uploadRejection(ticketId, file, entryDate) {
    return this.ticketDataService
      .uploadRejection(ticketId, file, entryDate)
      .then(this.getRefreshResponse(ticketId));
  }

  uploadTransactionData(ticketId, file, entryDate) {
    return this.ticketDataService
      .uploadTransactionData(ticketId, file, entryDate)
      .then(this.getRefreshResponse(ticketId));
  }

  uploadMiscDocument(ticketId, file) {
    return this.ticketDataService
      .uploadMisc(ticketId, file)
      .then(this.getRefreshResponse(ticketId));
  }

  rejectReceiving(ticketId) {
    return this.ticketDataService
      .rejectReceiving(ticketId)
      .then(this.getRefreshResponse(ticketId));
  }

  skipDownloadWarrentForm(ticketId) {
    return this.ticketDataService.skipDownloadWarrentForm(ticketId);
  }

  redirectWarrantForm(ticketId, skipRedirect) {
    return this.ticketDataService.redirectWarrantForm(ticketId, skipRedirect);
  }

  getWarrantWithSignature(ticketId, data) {
    return this.ticketDataService.getWarrantWithSignature(ticketId, data);
  }

  redirectSignedWarrantForm(fileId) {
    return this.ticketDataService.redirectSignedWarrantForm(fileId);
  }

  downloadDocument(url) {
    return this.ticketDataService.downloadDocument(url);
  }

  fetchTransactions(ticketType, ticketId) {
    return this.ticketDataService.fetchTransactions(ticketType, ticketId);
  }

  closeTicket(ticketType, ticketId, isDWS = false) {
    if (isDWS) {
      return this.cancelDWS(ticketId);
    }

    return this.ticketDataService
      .close(ticketType, ticketId)
      .then(this.getRefreshResponse(ticketId));
  }

  addComment(ticketId, data, product = ProductType.KWH, refresh = true) {
    return this.ticketDataService
      .addComment(ticketId, data)
      .then(this.getRefreshResponse(ticketId, !refresh, product));
  }

  putFinances(ticketId, data, refresh = true) {
    return this.ticketDataService
      .putFinances(ticketId, data)
      .then(this.getRefreshResponse(ticketId, !refresh));
  }

  bufferFinances(ticketId, data) {
    return this.ticketDataService.bufferFinances(ticketId, data);
  }

  putFinancesConfirm(ticketId, data) {
    return this.ticketDataService
      .putFinancesConfirm(ticketId, data)
      .then(this.getRefreshResponse(ticketId));
  }

  deleteUploadedBalanceList(ticketId) {
    return this.ticketDataService
      .deleteUploadedBalanceList(ticketId)
      .then(this.getRefreshResponse(ticketId));
  }

  putFinancesOutgoing(ticketId, data) {
    return this.ticketDataService
      .putFinancesOutgoing(ticketId, data)
      .then(this.getRefreshResponse(ticketId));
  }

  setAccountCloseDate(ticketId, data) {
    return this.ticketDataService
      .setAccountCloseDate(ticketId, data)
      .then(this.getRefreshResponse(ticketId));
  }

  processStandingOrdersWithRemark(ticketId, data) {
    return this.ticketDataService
      .processStandingOrdersWithRemark(ticketId, data)
      .then(this.getRefreshResponse(ticketId));
  }

  processStandingOrders(ticketId, data) {
    return this.ticketDataService
      .processStandingOrders(ticketId, data)
      .then(this.getRefreshResponse(ticketId));
  }

  updateStandingOrderStatus(ticketId, orderId, data) {
    return this.ticketDataService
      .updateStandingOrderStatus(ticketId, orderId, data)
      .then(this.getRefreshResponse(ticketId));
  }

  uploadStandingOrdersDemoData(ticketId, file) {
    return this.ticketDataService
      .uploadStandingOrdersDemoData(ticketId, file)
      .then(this.getRefreshResponse(ticketId));
  }

  forceStandingOrdersUpdate(ticketId, orderId, conflictReason) {
    return this.ticketDataService
      .forceStandingOrdersUpdate(ticketId, orderId, conflictReason)
      .then(this.getRefreshResponse(ticketId));
  }

  forceStandingOrdersWithExDate(ticketId, orderId) {
    return this.ticketDataService
      .forceStandingOrdersWithExDate(ticketId, orderId)
      .then(this.getRefreshResponse(ticketId));
  }

  sendStandingOrderWithConfirmedCrossBorder(ticketId, orderId, bic){
    return this.ticketDataService
      .sendStandingOrderWithConfirmedCrossBorder(ticketId, orderId, bic)
      .then(this.getRefreshResponse(ticketId));
  }

  updateStandingOrder(ticketId, standingOrder) {
    return this.ticketDataService
      .updateStandingOrder(ticketId, standingOrder)
      .then(this.getRefreshResponse(ticketId));
  }

  updatePaymentPartner(ticketId, data) {
    return this.ticketDataService
      .updatePaymentPartner(ticketId, data)
      .then(this.getRefreshResponse(ticketId));
  }

  completeOutgoing(ticketId) {
    return this.ticketDataService
      .completeOutgoing(ticketId)
      .then(this.getRefreshResponse(ticketId));
  }

  completeIncoming(ticketId) {
    return this.ticketDataService
      .completeIncoming(ticketId)
      .then(this.getRefreshResponse(ticketId));
  }

  outgoingDataComplete(ticketId) {
    return this.ticketDataService
      .outgoingDataComplete(ticketId)
      .then(this.getRefreshResponse(ticketId));
  }

  transactionDataSkip(ticketId) {
    return this.ticketDataService
      .transactionDataSkip(ticketId)
      .then(this.getRefreshResponse(ticketId));
  }

  initializationConfirmation(ticketId, data) {
    return this.ticketDataService
      .initializationConfirmation(ticketId, data)
      .then(this.getRefreshResponse(ticketId));
  }

  initializationConfirmationByCustomer(ticketId, data) {
    return this.ticketDataService
      .initializationConfirmationByCustomer(ticketId, data)
      .then(this.getRefreshResponse(ticketId));
  }

  downloadStandingOrdersCsv(ticketId) {
    return this.ticketDataService.downloadStandingOrdersCsv(ticketId);
  }

  downloadStandingOrdersJson(ticketId) {
    return this.ticketDataService.downloadStandingOrdersJson(ticketId);
  }

  postStandingOrders(ticketId, standingOrders) {
    return this.ticketDataService
      .postStandingOrders(ticketId, standingOrders)
      .then(this.getRefreshResponse(ticketId));
  }

  getExternalLink(ticket) {
    if (ticket) {
      return _.find(this.ExternalLinking, (el) => {
        // Check ticket status against the states defined in the external linking entry
        if (el.states && _.includes(el.states, ticket.status)) {
          // Check if the external linking has a condition and check that condition
          if (angular.isFunction(el.condition)) {
            return el.condition(ticket);
          }
          // Add external linking entry
          return true;
        }
        // Ignore external linking entry
        return false;
      });
    }
  }

  accountSwitchPackage(ticketId, ticketType) {
    return this.ticketDataService.accountSwitchPackage(ticketId, ticketType);
  }

  hide(ticketId, ticketType) {
    return this.ticketDataService.hide(ticketId, ticketType);
  }

  resendDocument(ticketId, ticketType, doc) {
    return this.ticketDataService.resendDocument(
      ticketId,
      ticketType,
      doc.type,
      doc.documentId,
    );
  }

  informPaymentPartners(ticketId) {
    return this.ticketDataService.informPaymentPartners(ticketId);
  }

  simulateFinancesDataCaptured(ticketId) {
    return this.ticketDataService.simulateFinancesDataCaptured(ticketId);
  }

  changeEmployeeStatus(ticketType, ticketId, data) {
    return this.ticketDataService.changeEmployeeStatus(
      ticketId,
      ticketType,
      data,
    );
  }

  resetToSentRequestToTransferring(ticketId) {
    return this.ticketDataService.resetToSentRequestToTransferring(ticketId);
  }

  uploadAdditionalDocuments(ticketId, data) {
    return this.ticketDataService.uploadAdditionalDocuments(ticketId, data);
  }

  toggleRemindersWithRefresh(ticketId, paused) {
    return this.toggleReminders(ticketId, paused).then(
      this.getRefreshResponse(ticketId),
    );
  }

  downloadArchiveManually(ticketId) {
    return this.ticketDataService.downloadArchiveManually(ticketId);
  }

  markAsArchivedManually(ticketId) {
    return this.ticketDataService
      .markAsArchivedManually(ticketId)
      .then(this.getRefreshResponse(ticketId));
  }

  toggleReminders(ticketId, paused) {
    if (paused) {
      return this.ticketDataService.resumeReminders(ticketId);
    }

    return this.ticketDataService.pauseReminders(ticketId);
  }
}
